// <app setup>
import "./clientlogger"
import { ClientLogger } from "./clientlogger"

// Initialize the client logger as soon as possible
const clientLogger = new ClientLogger()

export { SYSFormElement } from "./library/forms/sysformelement"
export { SYSBaseElement } from "./library/sysbaseelement"

import { SYSConfig, app, appState } from "../../_dev/client/app/src/appdef"

import {
  UnsafeHTMLDirective,
  unsafeHTML as LITUnsafeHTML,
} from "lit-html/directives/unsafe-html.js"

type SYSAppDialogs =
  | keyof typeof app.dialogs
  | keyof typeof app.editors
  | keyof typeof baseEditors

type SYSAppForms =
  | keyof typeof app.forms
  | keyof typeof app.editors
  | keyof typeof baseEditors

type SYSAppTableDefIds = keyof typeof app.tableDefIds

export type CustomValidatorsType = keyof typeof app.customValidators

type SYSAppEditors = keyof typeof app.editors
type SYSAppScreens = keyof typeof app.screens

type SYSAppPermissions =
  | "SYSPA"
  | "SYSUA"
  | "SYSRA"
  | "SYSCA"
  | "SYSUR"
  | "SYSAU"
  | "SYSTR"
  | "SYSTS"
  | "SYSST"
  | keyof typeof app.permissions
type SYSAppFeatures =
  | "SYSUserAdmin"
  | "SYSRoleAdmin"
  | "SYSUserRoleAdmin"
  | "SYSCompanyAdmin"
  | keyof typeof app.features
type SYSAppLongRunningProcess = keyof typeof app.longRunningProcess
type SYSFormValidationValidators =
  | keyof typeof validatorsValidation
  | keyof typeof app.customValidators

export { SYSConfig }

import { startRender } from "../../_dev/client/app/src"
import { appActions } from "../../_dev/client/app/src/appdef"
import { ovlActions } from "./actions"
export type SYSAppFeature = keyof typeof appActions.features | "SYSTOTP"

import { validatorsValidation } from "./appdefformvalidation"
import {
  baseDialogs,
  baseEditors,
  baseForms,
  baseLongRunningProcess,
  baseScreens,
} from "./baseobjects"
import {
  _registerComponents,
  logActions,
  logFormValidationErrors,
  logRenderTracking,
  logState,
  logVersion,
  rehydrateUserState,
  rehydrateUserStateFromFile,
  saveUserState,
  toggleDebugTracking,
} from "./global/globals"
import { HookAppRouting, HookAppStateReady } from "./global/hooks"
export { SAPObjType } from "./global/sap"
export type { SYSSAPObjType } from "./global/sap"
import { ScreenCurrent, ScreenNavigateTo } from "./library/screenapi"
import type { SYSTableData } from "./library/table/table"
import { sessionStore } from "./offlinestorage"
import {
  /*addDefaultReactors,*/
  defineSYSElements,
  init,
  registerElements,
} from "./prepare"
import { ovlState } from "./state"
import { sysComponentsActions } from "./syscomponentsactions"
import { StartScreenUpdate, createDeepProxy } from "./tracker/proxyhandler"
import { logTrackingList, testReactorsPaths } from "./tracker/tracker"
import { SYSEPT_Init } from "../ovlsyscomponents/sysept01/compdef"
import { SYSLandingPage01_Init } from "../ovlsyscomponents/syslandingpage01/compdef"
import { SYSUserSettingsPassword_Init } from "../ovlsyscomponents/sysusersettings01/password/compdef"
import { SYSTableDetailView_Init } from "../ovlsyscomponents/systabledetailview01/compdef"

export type { SYSFormValidationValidators }
//IE won't work
//@ts-ignore
if (window.document.documentMode) {
  alert("Internet Explorer not supported. Upgrade to Edge Browser")
  window.location.href = "https://www.microsoft.com/de-de/edge"
}

// some global vars which help debugging as well in live environment in Desktop Browsers (F12)
globalThis.OvlLogActions = logActions
globalThis.OvlLogState = logState
globalThis.OvlLogRenderTracking = logRenderTracking
globalThis.OvlToggleDebugTracking = toggleDebugTracking
globalThis.OvlLogTrackingList = logTrackingList
globalThis.OvlTestReactorsPaths = testReactorsPaths
globalThis.OvlSaveUserState = saveUserState
globalThis.OvlRehydrateUserState = rehydrateUserState
globalThis.OvlRehydrateUserStateFromFile = rehydrateUserStateFromFile
globalThis.OvlLogFormValidationErrors = logFormValidationErrors
globalThis.OvlConfig = SYSConfig
globalThis.OvlVersion = logVersion
export type SYSState = {
  ovl: typeof ovlState
  app: typeof appState
}

export type SYSActions = {
  ovl: typeof ovlActions
  app: typeof appActions
}
export type SYSHelpers = {
  tablesGetData: {
    [key in SYSAppTableDefIds]: (state: SYSState) => SYSTableData
  }
}
export type HookAppRouting_Type = {
  fromInit: boolean
  urlParams: URLSearchParams
}

export const ovl: {
  getState: () => SYSState
  getActions: () => SYSActions
} = {
  getState: () => _ovl.state,
  getActions: () => _ovl.actions,
}

let _ovl: {
  state: SYSState
  actions: SYSActions
} = init(
  appState,
  appActions,
  SYSConfig.app.screens,
  SYSConfig.app.dialogs,
  SYSConfig.app.longRunningProcess,
  ovlState,
  ovlActions,
  baseScreens,
  baseDialogs,
  baseLongRunningProcess,
  SYSConfig.app.forms,
  baseForms,
  SYSConfig.app.editors,
  baseEditors,
  app.customValidators,
  sysComponentsActions
)
defineSYSElements()
SYSUserSettingsPassword_Init()
SYSEPT_Init()
SYSLandingPage01_Init()
SYSTableDetailView_Init()

globalThis.Ovl = _ovl

export let currentScreenAndDialog = {
  screens: [],
  dialogs: {},
  dialogFocusSet: { dialog: "", instance: "" },
}
setTimeout(async () => {
  try {
    await ovl.getActions().ovl.internal._InitApp()
  } catch (e) {
    console.log(e)
    let message = "Ovl Initialisation Error"
    if (e.message) {
      message = `${message}: ${e.message}`
    }
    document.getElementById("app").innerHTML = message
    return
  }
  let state = (_ovl.state = <SYSState>createDeepProxy(ovl.getState()))

  let bodyEl = document.getElementsByTagName("body")[0]
  if (state.ovl.uiState.isDemo) {
    bodyEl.classList.add("ovl-isdemo")
  }

  if (_registerComponents._customElementList.length > 0) {
    registerElements()
  }

  // update ovl.state.session with persisted value

  let persistedSessionObj = await sessionStore.get("base")
  if (persistedSessionObj !== undefined) {
    Object.keys(persistedSessionObj).forEach((key) => {
      state.ovl.session[key] = persistedSessionObj[key]
    })
  }

  await startRender(state)
  StartScreenUpdate(0)
  //addDefaultReactors()
  if (state.ovl.uiState.appRehydrated) {
    let stateReadyFn = ovl.getActions().app.global?.hooks?.[HookAppStateReady]
    if (stateReadyFn) {
      await stateReadyFn()
      if (_registerComponents._customElementList.length > 0) {
        registerElements()
      }

      state.ovl.app.ready = true
      let routeFn = ovl.getActions().app.global?.hooks?.[HookAppRouting]
      if (routeFn) {
        await routeFn({
          fromInit: true,
          urlParams: new URLSearchParams(state.ovl.app.urlQueryString),
        } as HookAppRouting_Type)
      }
    }
  } else {
    setTimeout(async () => {
      const queryString = window.location.search
      const urlParams = new URLSearchParams(queryString)
      state.ovl.app.urlQueryString = queryString
      if (state.ovl.app.cleanUrl) {
        window.history.replaceState({}, document.title, "/")
      }

      if (_registerComponents._customElementList.length > 0) {
        registerElements()
      }

      if (urlParams.get("ovllandingpage01")) {
        await ScreenNavigateTo("SYSLandingPage01")
        //state.ovl.app.ready = true
        return
      }
      let readyFn = ovl.getActions().app.global?.hooks?.HookAppReady
      if (readyFn) {
        await readyFn()
      }

      let stateReadyFn = ovl.getActions().app.global?.hooks?.[HookAppStateReady]
      if (stateReadyFn) {
        await stateReadyFn()
        if (_registerComponents._customElementList.length > 0) {
          registerElements()
        }
      }
      state.ovl.app.ready = true

      let routeFn = ovl.getActions().app.global?.hooks?.[HookAppRouting]
      if (routeFn) {
        await routeFn({
          fromInit: true,
          urlParams: new URLSearchParams(state.ovl.app.urlQueryString),
        } as HookAppRouting_Type)
      }
      if (!ScreenCurrent() && SYSConfig.init?.initalScreen) {
        await ScreenNavigateTo(SYSConfig.init.initalScreen)
      }
    }, 0)
  }
}, 0)

export type SYSSessionEntries = "Language" /*| "Token"*/

export type SYSForm = SYSAppForms | keyof typeof baseForms
export type SYSDialog = SYSAppDialogs | keyof typeof baseDialogs
export type SYSEditor = SYSAppEditors | keyof typeof baseEditors
export type SYSLongRunningProcess =
  | SYSAppLongRunningProcess
  | keyof typeof baseLongRunningProcess
export type SYSTableDefIds =
  | SYSAppTableDefIds
  | "SYSPortalSettings"
  | "SYSTranslation"
  | "SYSAudit"
  | "SYSEPT"
  | "SYSUserAdministration_User"
  | "SYSUserAdministration_UserRoleTransfer"
  | "SYSUserAdministration_Role"
  | "SYSUserAdministration_Permission"
  | "SYSUserAdministration_ResultingPermission"
  | "SYSUserAdministration_UserRole"
  | "SYSUserAdministration_Company"
  | "SYSTableSettings_FilterSort"
  | "SYSTableSettings_ColumnFilter"

export type SYSFeature =
  | SYSAppFeature
  | keyof typeof sysComponentsActions.features
  | "SYSTableSettings"
export type SYSScreen =
  | SYSAppScreens
  | keyof typeof sysComponentsActions.screens
  | keyof typeof baseScreens
export type SYSPermissions = SYSAppPermissions
export type SYSFeatures = "SYSPortalTesting" | SYSAppFeatures

type SYSActionContext = {
  global: { getState: () => SYSState; getActions: () => SYSActions }
  context: { getState: () => SYSState; getActions: () => SYSActions }
  execution?: {
    handled: boolean
    currentRes: any
    // use data to hand over data from before to main action or to after action
    // like when the user selects an option in the before which is also relevant for the following actions

    data: any
    cancel: () => void
  }
}

export { html, render, svg } from "lit-html"
export type { TemplateResult } from "lit-html"
export { directive } from "lit-html/directive.js"
export type { DirectiveResult } from "lit-html/directive.js"
export { ifDefined } from "lit-html/directives/if-defined.js"
export { repeat } from "lit-html/directives/repeat.js"
export { UnsafeHTMLDirective }
export const unsafeHTML = (html: string) => {
  // a very specific ovl sanitizer
  // we already have strict csp nonce rules for script
  // so this simple checks should be fine

  // Returns "insecure" if obviously dangerous patterns are detected,
  // otherwise returns the original HTML.
  if (html) {
    const lower = html.toLowerCase()

    // 1) Inline event handlers: onclick=, onload=, onerror=, ...
    const hasInlineEvents = /\son[a-z]+\s*=/.test(lower)

    // 2) Dangerous URL schemes in common URL attributes (quoted)
    const hasBadUrlQuoted =
      /(href|src|xlink:href|formaction|action)\s*=\s*(['"])\s*(javascript:|data:)/.test(
        lower
      )

    // 3) Dangerous URL schemes in common URL attributes (unquoted)
    const hasBadUrlUnquoted =
      /(href|src|xlink:href|formaction|action)\s*=\s*(javascript:|data:)/.test(
        lower
      )

    // 4) Attributes your own code might treat as navigation/redirect gadgets
    //    (adapt the list to what your code actually uses)
    const hasSuspiciousDataAttrs = /\sdata-(url|redirect|target-url)\s*=/.test(
      lower
    )

    if (
      hasInlineEvents ||
      hasBadUrlQuoted ||
      hasBadUrlUnquoted ||
      hasSuspiciousDataAttrs
    ) {
      return "insecure"
    }
  }
  return LITUnsafeHTML(html)
}

// export {
//   UnsafeHTMLDirective,
//   unsafeHTML,
// } from "lit-html/directives/unsafe-html.js"

export type SYSAction<T = {}, R = void> = (
  value?: T,
  context?: SYSActionContext
) => R
